import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAudits, saveAudit } from '../utils/storage';
import { generateDemoData } from '../utils/demoData';
import { calculateNextIndex, getScoreColor, getTier } from '../utils/scoring';
import { formatDate } from '../utils/export';
import Onboarding, { shouldShowOnboarding, markOnboarded } from '../components/Onboarding';
import { useTheme, LOGOS } from '../utils/theme';

export default function Home() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(shouldShowOnboarding());
  const { theme } = useTheme();

  useEffect(() => {
    getAudits().then(data => {
      setAudits(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      {showOnboarding && (
        <Onboarding onComplete={() => { markOnboarded(); setShowOnboarding(false); }} />
      )}
      {/* Hero */}
      <div className="text-center py-16 sm:py-24">
        <img
          src={LOGOS[theme] || LOGOS.dark}
          alt="Next Experience Group"
          className="h-16 w-auto mx-auto mb-8"
        />
        <h1 className="font-display text-5xl sm:text-7xl text-white mb-4 tracking-wide">
          What's <span className="text-brand-gold">NEXT</span>
        </h1>
        <div className="w-16 h-[1px] bg-brand-gold mx-auto mb-6"></div>
        <p className="text-neutral-300 text-base sm:text-xl mb-2 font-light tracking-wide">Hospitality Business Health Intelligence</p>
        <p className="text-neutral-600 text-xs mb-12 tracking-[0.25em] uppercase">by Next Experience Group</p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/new" className="btn-primary text-base px-10 py-4">
            Start New Audit
          </Link>
          {audits.length > 0 && (
            <Link to="/dashboard" className="btn-secondary text-base px-10 py-4">
              View Dashboard
            </Link>
          )}
        </div>
          <button onClick={async () => {
            const demos = generateDemoData();
            for (const d of demos) await saveAudit(d);
            window.location.reload();
          }} className="mt-4 text-neutral-700 text-xs hover:text-neutral-400 transition-colors">
            Load Demo Company
          </button>
      </div>

      {/* What is it */}
      <div className="card p-8 mb-8">
        <h2 className="font-display text-xl text-brand-gold mb-4">The Next Index</h2>
        <p className="text-neutral-400 text-sm leading-relaxed mb-6">
          A proprietary 10-pillar diagnostic that scores your hospitality concept across every dimension
          that matters — from concept clarity and brand expression to emotional experience, menu intelligence,
          and growth readiness.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {['Concept', 'Brand', 'Guest Signal', 'Operations', 'Demand', 'Commercial', 'Growth', 'Emotion', 'Menu Intel', 'Beverage'].map((label, i) => (
            <div key={i} className="bg-neutral-800/50 border border-neutral-700/30 rounded-lg p-3 text-center hover:border-brand-gold/30 transition-all">
              <div className="w-8 h-8 rounded-full bg-brand-gold/15 flex items-center justify-center mx-auto mb-1.5">
                <span className="text-brand-gold text-xs font-bold">{i + 1}</span>
              </div>
              <p className="text-[10px] text-neutral-400 tracking-wide">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent audits */}
      {loading ? (
        <div className="text-center text-neutral-500 text-sm">Loading audits...</div>
      ) : audits.length > 0 && (
        <div>
          <h2 className="font-display text-lg text-white mb-4">Recent Audits</h2>
          <div className="grid gap-3">
            {audits.slice(0, 5).map((audit) => {
              const index = calculateNextIndex(audit.scores || {});
              const color = getScoreColor(index);
              const tier = getTier(index);
              return (
                <Link
                  key={audit.id}
                  to={`/dashboard/${audit.id}`}
                  className="card p-4 flex items-center gap-3 sm:gap-4 hover:border-brand-gold/30 active:scale-[0.99] transition-all"
                >
                  <div className={`w-12 h-12 sm:w-12 sm:h-12 rounded-lg ${color.bg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-lg font-black ${color.text}`}>{index.toFixed(1)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-white text-sm sm:text-base truncate">{audit.intake?.conceptName || 'Untitled'}</p>
                      <span className={`text-[9px] px-2 py-0.5 rounded font-medium flex-shrink-0 ${
                        audit.status === 'delivered' ? 'bg-emerald-500/15 text-emerald-400' :
                        audit.status === 'review' ? 'bg-amber-500/15 text-amber-400' :
                        audit.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                        audit.status === 'follow_up' ? 'bg-purple-500/15 text-purple-400' :
                        'bg-neutral-800 text-neutral-500'
                      }`}>
                        {audit.status === 'in_progress' ? 'In Progress' : audit.status === 'follow_up' ? 'Follow-Up' : (audit.status || 'Draft').charAt(0).toUpperCase() + (audit.status || 'draft').slice(1)}
                      </span>
                    </div>
                    <p className="text-[11px] sm:text-xs text-neutral-500">{audit.intake?.city}{audit.intake?.companyTag ? ` — ${audit.intake.companyTag}` : ''} — {formatDate(audit.createdAt)}</p>
                  </div>
                  <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full font-medium hidden sm:inline" style={{ backgroundColor: tier.color + '20', color: tier.color }}>
                    {tier.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
